import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import { LuCheck, LuLoaderCircle, LuPercent, LuSearch, LuTrendingUp } from 'react-icons/lu';
import { db } from '../../../config/firebaseConfig';
import { TextInput } from '../../../components/form/FormControls';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { calculateGrossFromNet } from '../salaryMath';
import useEmployeeFinanceText from './employeeFinanceText';

const QUICK_PERCENTAGES = ['5', '10', '15', '20', '25'];
const CHECKBOX = 'tw-h-4 tw-w-4 tw-shrink-0 tw-cursor-pointer tw-rounded tw-border-slate-300 tw-accent-teal-600 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/40';

/**
 * Toplu Maaş Zammı: a raise in percent for the chosen active employees with
 * a salary. Every chosen employee gets a new salary record (net raised,
 * gross and costs recalculated, contract date today), as before; the
 * confirmation is a dialog instead of the browser's confirm.
 */
const ZamModal = ({ onClose }) => {
  const { language, locale, label, formatMoney } = useEmployeeFinanceText();
  const [employees, setEmployees] = useState([]);
  const [employeeSalaries, setEmployeeSalaries] = useState({});
  const [selectedEmployees, setSelectedEmployees] = useState({});
  const [raisePercentage, setRaisePercentage] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const fetchEmployeesWithSalaries = async () => {
      try {
        setLoading(true);
        const employeesSnapshot = await getDocs(collection(db, 'Employees'));
        const employeesData = employeesSnapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));

        const salariesSnapshot = await getDocs(collection(db, 'Salaries'));
        const salariesData = salariesSnapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));

        // Latest salary per employee (updatedAt or createdAt)
        const salariesByEmployee = {};
        salariesData.forEach((salary) => {
          if (!salariesByEmployee[salary.employeeId]) {
            salariesByEmployee[salary.employeeId] = salary;
          } else {
            const currentTime = salariesByEmployee[salary.employeeId].updatedAt || salariesByEmployee[salary.employeeId].createdAt || 0;
            const newTime = salary.updatedAt || salary.createdAt || 0;
            if (newTime > currentTime) salariesByEmployee[salary.employeeId] = salary;
          }
        });

        // Active employees who have a salary
        const employeesWithSalaries = employeesData
          .filter((emp) => salariesByEmployee[emp.id] && emp.Active === 1)
          .sort((a, b) => `${a.eName || ''} ${a.eSurname || ''}`.localeCompare(`${b.eName || ''} ${b.eSurname || ''}`, 'tr'));

        setEmployees(employeesWithSalaries);
        setEmployeeSalaries(salariesByEmployee);

        // Everyone is selected at first
        const initialSelection = {};
        employeesWithSalaries.forEach((emp) => { initialSelection[emp.id] = true; });
        setSelectedEmployees(initialSelection);
      } catch (error) {
        console.error('Error fetching employees with salaries:', error);
        toast.error(label('raise.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchEmployeesWithSalaries();
    // Yalnızca açılışta
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleEmployee = (employeeId) => setSelectedEmployees((prev) => ({ ...prev, [employeeId]: !prev[employeeId] }));

  const handleSelectAll = () => {
    const allSelected = {};
    employees.forEach((emp) => { allSelected[emp.id] = true; });
    setSelectedEmployees(allSelected);
  };

  const handleDeselectAll = () => setSelectedEmployees({});

  const hasPercentage = Boolean(raisePercentage) && !Number.isNaN(Number(raisePercentage));

  const getNewSalaryPreview = (employeeId) => {
    const currentSalary = employeeSalaries[employeeId];
    // Only for selected employees
    if (!selectedEmployees[employeeId] || !currentSalary || !hasPercentage) return null;
    const currentNet = parseFloat(currentSalary.netSalary);
    const newNet = currentNet * (1 + parseFloat(raisePercentage) / 100);
    return { current: currentNet, new: newNet, difference: newNet - currentNet };
  };

  const selectedCount = Object.values(selectedEmployees).filter(Boolean).length;

  const summary = useMemo(() => {
    let current = 0;
    let next = 0;
    employees.forEach((emp) => {
      if (!selectedEmployees[emp.id]) return;
      const net = parseFloat(employeeSalaries[emp.id]?.netSalary) || 0;
      current += net;
      next += hasPercentage ? net * (1 + parseFloat(raisePercentage) / 100) : net;
    });
    return { current, next, difference: next - current };
  }, [employees, selectedEmployees, employeeSalaries, raisePercentage, hasPercentage]);

  const visibleEmployees = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr-TR');
    if (!term) return employees;
    return employees.filter((emp) => `${emp.eName || ''} ${emp.eSurname || ''} ${emp.departman || ''}`.toLocaleLowerCase('tr-TR').includes(term));
  }, [employees, search]);

  // Checks first, then asks (ConfirmDialog)
  const handleApplyClick = () => {
    if (selectedCount === 0) {
      toast.error(label('raise.noneSelected'));
      return;
    }
    if (!raisePercentage || Number.isNaN(Number(raisePercentage)) || parseFloat(raisePercentage) <= 0) {
      toast.error(label('raise.invalidPercent'));
      return;
    }
    setConfirming(true);
  };

  const handleApplyRaise = async () => {
    setApplying(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const timestamp = new Date().getTime(); // for sorting
      let successCount = 0;

      for (const employee of employees) {
        if (selectedEmployees[employee.id]) {
          const currentSalary = employeeSalaries[employee.id];
          const newNetSalary = parseFloat(currentSalary.netSalary) * (1 + parseFloat(raisePercentage) / 100);
          const calculations = calculateGrossFromNet(newNetSalary, { empty: 0 });

          const newSalaryData = {
            employeeId: employee.id,
            contractDate: today,
            netSalary: newNetSalary.toFixed(2),
            grossSalary: calculations.grossSalary,
            totalDeductions: calculations.totalDeductions,
            suggestedCost: calculations.totalEmployerCost,
            createdAt: timestamp,
          };

          await addDoc(collection(db, 'Salaries'), newSalaryData);
          successCount++;
        }
      }

      toast.success(label('raise.applied', { count: successCount }));
      setConfirming(false);
      onClose();
    } catch (error) {
      console.error('Error applying raise:', error);
      toast.error(label('raise.error'));
    } finally {
      setApplying(false);
    }
  };

  let list;
  if (loading) {
    list = (
      <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-10 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
        <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
        {label('common.loading')}
      </p>
    );
  } else if (employees.length === 0) {
    list = <p className="tw-py-10 tw-text-center tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('raise.empty')}</p>;
  } else {
    list = (
      <ul className="tw-divide-y tw-divide-slate-200/70 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200/80 dark:tw-divide-white/[0.06] dark:tw-border-white/10">
        <li aria-hidden="true" className="tw-hidden tw-grid-cols-[2rem_minmax(0,1.6fr)_minmax(0,1fr)_repeat(3,minmax(0,1fr))] tw-gap-3 tw-bg-slate-50/80 tw-px-4 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500 md:tw-grid dark:tw-bg-white/[0.03] dark:tw-text-slate-400">
          <span />
          <span>{label('raise.columns.employee')}</span>
          <span>{label('raise.columns.department')}</span>
          <span className="tw-text-right">{label('raise.columns.current')}</span>
          <span className="tw-text-right">{label('raise.columns.next')}</span>
          <span className="tw-text-right">{label('raise.columns.difference')}</span>
        </li>
        {visibleEmployees.map((employee) => {
          const preview = getNewSalaryPreview(employee.id);
          const currentSalary = employeeSalaries[employee.id];
          const checked = Boolean(selectedEmployees[employee.id]);
          const name = `${employee.eName || ''} ${employee.eSurname || ''}`.trim();
          return (
            <li key={employee.id}>
              <label className={`tw-grid tw-cursor-pointer tw-grid-cols-[2rem_minmax(0,1fr)_auto] tw-items-center tw-gap-x-3 tw-gap-y-0.5 tw-px-4 tw-py-3 tw-transition-colors md:tw-grid-cols-[2rem_minmax(0,1.6fr)_minmax(0,1fr)_repeat(3,minmax(0,1fr))] ${checked ? 'tw-bg-teal-500/[0.05]' : 'hover:tw-bg-slate-50/70 dark:hover:tw-bg-white/[0.02]'}`}>
                <input type="checkbox" checked={checked} onChange={() => handleToggleEmployee(employee.id)} className={CHECKBOX} />
                <span className="tw-min-w-0">
                  <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{name || '-'}</span>
                  <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 md:tw-hidden dark:tw-text-slate-400">{employee.departman || '-'}</span>
                </span>
                <span className="tw-hidden tw-truncate tw-text-sm tw-text-slate-600 md:tw-block dark:tw-text-slate-300">{employee.departman || '-'}</span>
                <span className="tw-hidden tw-text-right tw-text-sm tw-tabular-nums tw-text-slate-600 md:tw-block dark:tw-text-slate-300">{formatMoney(currentSalary.netSalary)}</span>
                <span className="tw-text-right tw-text-sm tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">
                  {preview ? formatMoney(preview.new) : formatMoney(currentSalary.netSalary)}
                  <span className="tw-block tw-text-xs tw-font-medium tw-text-emerald-600 md:tw-hidden dark:tw-text-emerald-300">{preview ? `+${formatMoney(preview.difference)}` : ''}</span>
                </span>
                <span className="tw-hidden tw-text-right tw-text-sm tw-font-semibold tw-tabular-nums tw-text-emerald-600 md:tw-block dark:tw-text-emerald-300">
                  {preview ? `+${formatMoney(preview.difference)}` : '-'}
                </span>
              </label>
            </li>
          );
        })}
        {visibleEmployees.length === 0 && <li className="tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('raise.noMatch')}</li>}
      </ul>
    );
  }

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        size="xl"
        dismissible={!applying}
        title={label('raise.title')}
        subtitle={label('raise.subtitle')}
        closeLabel={label('common.cancel')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
            <LuTrendingUp aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={(
          <>
            <Button variant="ghost" onClick={onClose} disabled={applying}>{label('common.cancel')}</Button>
            <Button variant="primary" onClick={handleApplyClick} disabled={loading || employees.length === 0 || applying}>
              <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('raise.apply')}
            </Button>
          </>
        )}
      >
        <div className="tw-space-y-4">
          <div className="tw-grid tw-gap-3 lg:tw-grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
            <section className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
              <label htmlFor="raise-percent" className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
                <LuPercent aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                {label('raise.percent')}
              </label>
              <div className="tw-relative tw-mt-2">
                <TextInput
                  id="raise-percent"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.1"
                  value={raisePercentage}
                  onChange={(event) => setRaisePercentage(event.target.value)}
                  placeholder={label('raise.percentPlaceholder')}
                  className="tw-h-11 tw-pr-10 tw-text-lg tw-font-semibold tw-tabular-nums"
                />
                <span className="tw-pointer-events-none tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-base tw-font-semibold tw-text-slate-400">%</span>
              </div>
              <div className="tw-mt-2.5 tw-flex tw-flex-wrap tw-gap-1.5">
                {QUICK_PERCENTAGES.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setRaisePercentage(value)}
                    aria-pressed={raisePercentage === value}
                    className={`tw-h-7 tw-rounded-full tw-border tw-px-3 tw-text-xs tw-font-semibold tw-tabular-nums tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/40 ${raisePercentage === value ? 'tw-border-teal-500/40 tw-bg-teal-500/10 tw-text-teal-800 dark:tw-text-teal-200' : 'tw-border-slate-200 tw-bg-white/60 tw-text-slate-600 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-300'}`}
                  >
                    %{value}
                  </button>
                ))}
              </div>
            </section>
            <section aria-label={label('raise.summary')} className="tw-grid tw-grid-cols-3 tw-gap-2 tw-rounded-2xl tw-border tw-border-amber-500/20 tw-bg-amber-500/[0.06] tw-p-4">
              {[
                ['selected', `${selectedCount.toLocaleString(locale)} / ${employees.length.toLocaleString(locale)}`],
                ['currentTotal', formatMoney(summary.current)],
                ['newTotal', formatMoney(summary.next)],
              ].map(([key, value]) => (
                <div key={key} className="tw-min-w-0">
                  <p className="tw-truncate tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-amber-800/80 dark:tw-text-amber-200/80">{label(`raise.${key}`)}</p>
                  <p className="tw-mt-1 tw-truncate tw-text-base tw-font-semibold tw-tabular-nums tw-text-slate-900 sm:tw-text-lg dark:tw-text-white">{value}</p>
                </div>
              ))}
              {hasPercentage && summary.difference > 0 && (
                <p className="tw-col-span-3 tw-text-xs tw-font-semibold tw-text-emerald-700 dark:tw-text-emerald-300">
                  {label('raise.monthlyDifference', { amount: formatMoney(summary.difference) })}
                </p>
              )}
            </section>
          </div>

          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <label className="tw-relative tw-min-w-0 tw-flex-1">
              <span className="tw-sr-only">{label('raise.search')}</span>
              <LuSearch aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
              <TextInput value={search} onChange={(event) => setSearch(event.target.value)} placeholder={label('raise.search')} className="tw-pl-9 sm:tw-max-w-xs" />
            </label>
            <Button size="sm" onClick={handleSelectAll}>{label('raise.selectAll')}</Button>
            <Button size="sm" variant="ghost" onClick={handleDeselectAll}>{label('raise.deselectAll')}</Button>
          </div>

          {list}
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirming}
        title={label('raise.confirmTitle')}
        message={label('raise.confirmMessage', { count: selectedCount, percent: raisePercentage })}
        confirmLabel={label('raise.apply')}
        cancelLabel={label('common.cancel')}
        tone="warning"
        icon={LuTrendingUp}
        busy={applying}
        onConfirm={handleApplyRaise}
        onCancel={() => !applying && setConfirming(false)}
      />
    </>
  );
};

ZamModal.propTypes = { onClose: PropTypes.func.isRequired };

export default ZamModal;
